// Layer 3 (Transformations): Documents (Modules 7 + 8) — paul-tarp.A20 (#422), `a20-documents` stream.
// Pure functions only: no React, no engine mutations, no side effects.

import type { SurfacePageTabItem } from '@/design-system';
import type { Persona } from '@/shared/types';
import {
  DEFAULT_DOCUMENT_BY_PERSONA,
  INTERLOCKS,
  PROCEDURES,
  ROUND_SHEETS,
  TRAINING_MODULES,
  allDocuments,
  documentById,
  interlockById,
  type DocumentGroupId,
  type DocumentRecord,
  type InterlockRef,
  type ProcedureDoc,
  type RoundTask,
  type TrainingDoc,
} from './seed';
import {
  documentSignoffs,
  pendingPublishEffects,
  pendingVersionOf,
  signoffMatrix,
  type ComparisonSegment,
  type DocumentSignoffs,
  type PublishEffect,
} from './TrainingCompliance.transform';
import { pendingChanges } from './PendingChanges.transform';

export function documentsCopy() {
  return { title: 'Documents' };
}

// ---------------------------------------------------------------------------
// Tabs — M8's roster is the default view; the persona axis stays on the
// selected document inside the Procedure library tab.
// ---------------------------------------------------------------------------

export type DocumentsTab = 'compliance' | 'library' | 'ai';

export const DOCUMENT_TABS: readonly SurfacePageTabItem<DocumentsTab>[] = [
  { id: 'compliance', label: 'Training compliance' },
  { id: 'library', label: 'Procedure library' },
  { id: 'ai', label: 'AI update suggestions' },
];

/** Resolve a Paul's-tab query id; unknown values leave the current tab alone. */
export function documentsTabFromSearchParam(value: string | null): DocumentsTab | undefined {
  return DOCUMENT_TABS.find((tab) => tab.id === value)?.id;
}

/**
 * Display-only remap of the risk seed's `OverrideAuthority` vocabulary to the brief's chip
 * wording — 'No one' authorized to override reads as "No override" on the glass (MF1).
 */
export function displayOverrideAuthority(authority: InterlockRef['overrideAuthority']): string {
  return authority === 'No one' ? 'No override' : authority;
}

// ---------------------------------------------------------------------------
// documentTree — left-pane grouped index
// ---------------------------------------------------------------------------

export type DocumentTreeItem = {
  id: string;
  title: string;
  version: string;
  updatedAt: string;
  hasAttention: boolean;
  /** The version a pending revision publishes as (`v4`), when one is in review. */
  pendingVersion?: string;
  /** Sign-off coverage on the published version: `7 of 9 signed · 2 stale`; overdue counts unsigned past due. */
  signed: { current: number; required: number; stale: number; overdue: number };
};

export type DocumentTreeSection = {
  id: string;
  label: string;
  documents: DocumentTreeItem[];
};

export type DocumentTreeGroup = {
  id: DocumentGroupId;
  label: string;
  /** Render subgroup labels (Procedures) vs a single flat section (Round sheets, Training material). */
  showSectionLabels: boolean;
  sections: DocumentTreeSection[];
};

function treeItemFor(doc: DocumentRecord): DocumentTreeItem {
  const hasAttention = doc.kind === 'training' && Boolean(doc.pendingRevision);
  const signoffs = documentSignoffs(doc.id);
  const pendingVersion = pendingVersionOf(doc);
  return {
    id: doc.id,
    title: doc.title,
    version: doc.version,
    updatedAt: doc.updatedAt,
    hasAttention,
    ...(pendingVersion ? { pendingVersion } : {}),
    signed: {
      current: signoffs?.current ?? 0,
      required: signoffs?.required ?? 0,
      stale: signoffs?.stale.length ?? 0,
      overdue: (signoffs?.unsigned ?? [])
        .filter((group) => group.pastDue)
        .reduce((sum, group) => sum + group.names.length, 0),
    },
  };
}

export function documentTree(): DocumentTreeGroup[] {
  const procedureOrder = ['Start-up', 'Shutdown', 'Routine', 'Emergency'] as const;
  const procedureSections: DocumentTreeSection[] = procedureOrder.map((type) => ({
    id: type.toLowerCase(),
    label: type,
    documents: PROCEDURES.filter((doc) => doc.procedureType === type).map(treeItemFor),
  }));

  return [
    {
      id: 'procedures',
      label: 'Procedures',
      showSectionLabels: true,
      sections: procedureSections,
    },
    {
      id: 'round-sheets',
      label: 'Round sheets',
      showSectionLabels: false,
      sections: [{ id: 'round-sheets', label: 'Round sheets', documents: ROUND_SHEETS.map(treeItemFor) }],
    },
    {
      id: 'training',
      label: 'Training material',
      showSectionLabels: false,
      sections: [{ id: 'training', label: 'Training material', documents: TRAINING_MODULES.map(treeItemFor) }],
    },
  ];
}

// ---------------------------------------------------------------------------
// defaultDocumentFor — the persona picker chooses the default document
// ---------------------------------------------------------------------------

export function defaultDocumentFor(persona: Persona): string {
  return DEFAULT_DOCUMENT_BY_PERSONA[persona] ?? 'PR-01';
}

// ---------------------------------------------------------------------------
// documentView — center-pane view model (resolves interlock ids to full rows)
// ---------------------------------------------------------------------------

export type ProcedureStepView = {
  sequenceNo: number;
  instruction: string;
  safetyNote?: string;
  verification?: string;
  interlocks: InterlockRef[];
};

export type ProcedureView = Omit<ProcedureDoc, 'steps'> & { steps: ProcedureStepView[] };

export function documentView(id: string | null | undefined): DocumentRecord | ProcedureView | undefined {
  const doc = documentById(id);
  if (!doc) return undefined;
  if (doc.kind !== 'procedure') return doc;
  return {
    ...doc,
    steps: doc.steps.map((step) => ({
      ...step,
      interlocks: (step.interlockIds ?? [])
        .map((ilId) => interlockById(ilId))
        .filter((row): row is InterlockRef => Boolean(row)),
    })),
  };
}

// ---------------------------------------------------------------------------
// roundSheetProgress — this shift's completion
// ---------------------------------------------------------------------------

export type RoundSheetProgress = {
  done: number;
  total: number;
  flagged: RoundTask[];
  pending: RoundTask[];
};

/**
 * `shift` is accepted for signature-compatibility with a future multi-shift
 * RoundSheet (Module 7 §2 — `RoundSheet.shift`); the seed carries one shift's
 * tasks per document today, so every call reads that document's full list.
 */
export function roundSheetProgress(id: string, shift?: string): RoundSheetProgress {
  void shift;
  const doc = ROUND_SHEETS.find((row) => row.id === id);
  if (!doc) return { done: 0, total: 0, flagged: [], pending: [] };
  const tasks = doc.tasks;
  return {
    done: tasks.filter((t) => t.status === 'done').length,
    total: tasks.length,
    flagged: tasks.filter((t) => t.status === 'flagged'),
    pending: tasks.filter((t) => t.status === 'pending'),
  };
}

// ---------------------------------------------------------------------------
// revisionContext — right-pane context panel, shaped per document kind
// ---------------------------------------------------------------------------

export type ProcedureContext = {
  kind: 'procedure';
  signoffs?: DocumentSignoffs;
  sourceSections: string[];
  controlLoops: ProcedureDoc['controlLoops'];
  interlocks: InterlockRef[];
  changeEvents: (ProcedureDoc['changeEvents'][number] & { flagsTrainingTitle?: string })[];
};

export type RoundSheetContext = {
  kind: 'round-sheet';
  signoffs?: DocumentSignoffs;
  circuit: string;
  shiftLabel: string;
  progress: RoundSheetProgress;
  nextDue?: RoundTask;
  lastReading?: RoundTask;
};

/** The most recently logged reading (latest `dueAt` among completed tasks with a reading). */
function lastReadingFor(tasks: readonly RoundTask[]): RoundTask | undefined {
  const withReadings = tasks.filter((t) => t.status === 'done' && t.reading);
  if (withReadings.length === 0) return undefined;
  return withReadings.reduce((latest, task) => (task.dueAt > latest.dueAt ? task : latest));
}

export type TrainingContext = {
  kind: 'training';
  signoffs?: DocumentSignoffs;
  /** What publishing the pending revision does to the register. */
  publishEffect?: PublishEffect;
  audienceRole: string;
  sourceRefs: string[];
  versionHistory: TrainingDoc['versionHistory'];
  pendingRevision?: TrainingDoc['pendingRevision'];
  refresherTargets: TrainingDoc['refresherTargets'];
};

export type RevisionContext = ProcedureContext | RoundSheetContext | TrainingContext | undefined;

export function revisionContext(id: string | null | undefined): RevisionContext {
  const doc = documentById(id);
  if (!doc) return undefined;

  if (doc.kind === 'procedure') {
    const interlockIds = new Set(doc.steps.flatMap((step) => step.interlockIds ?? []));
    const changeEvents = doc.changeEvents.map((event) => ({
      ...event,
      flagsTrainingTitle: event.flagsTrainingId
        ? TRAINING_MODULES.find((tm) => tm.id === event.flagsTrainingId)?.title
        : undefined,
    }));
    return {
      kind: 'procedure',
      signoffs: documentSignoffs(doc.id),
      sourceSections: doc.sourceSections,
      controlLoops: doc.controlLoops,
      interlocks: [...interlockIds].map((ilId) => interlockById(ilId)).filter((row): row is InterlockRef => Boolean(row)),
      changeEvents,
    };
  }

  if (doc.kind === 'round-sheet') {
    const progress = roundSheetProgress(doc.id, doc.shiftLabel);
    const nextDue = doc.tasks.find((t) => t.status === 'pending');
    return {
      kind: 'round-sheet',
      signoffs: documentSignoffs(doc.id),
      circuit: doc.circuit,
      shiftLabel: doc.shiftLabel,
      progress,
      nextDue,
      lastReading: lastReadingFor(doc.tasks),
    };
  }

  const publishEffect = pendingPublishEffects().find((effect) => effect.documentId === doc.id);
  return {
    kind: 'training',
    signoffs: documentSignoffs(doc.id),
    ...(publishEffect ? { publishEffect } : {}),
    audienceRole: doc.audienceRole,
    sourceRefs: doc.sourceRefs,
    versionHistory: doc.versionHistory,
    pendingRevision: doc.pendingRevision,
    refresherTargets: doc.refresherTargets,
  };
}

export function allInterlocks(): readonly InterlockRef[] {
  return INTERLOCKS;
}

export function allDocumentIds(): readonly string[] {
  return allDocuments().map((doc) => doc.id);
}

// ---------------------------------------------------------------------------
// Header facts and tab headlines (locked comp `documents.html` B)
// ---------------------------------------------------------------------------

/** This shift's round-sheet checks, summed over every round sheet in the library. */
export function roundSheetsThisShift(): { done: number; total: number; flagged: RoundTask[] } {
  return ROUND_SHEETS.reduce(
    (sum, sheet) => {
      const progress = roundSheetProgress(sheet.id, sheet.shiftLabel);
      return {
        done: sum.done + progress.done,
        total: sum.total + progress.total,
        flagged: [...sum.flagged, ...progress.flagged],
      };
    },
    { done: 0, total: 0, flagged: [] as RoundTask[] },
  );
}

/** A flagged check's symptom as its sheet names it: the bad-looks-like line before its gloss. */
export function flaggedSymptom(task: RoundTask): string {
  return task.badLooksLike.split(' — ')[0] ?? task.badLooksLike;
}

export type DocumentsHeaderFact = {
  id: 'round-sheets' | 'next-publish';
  label: string;
  value: string;
  unit?: string;
  comparison: Array<ComparisonSegment & { tone?: 'warn' }>;
  /** A person who acts on the fact, drawn as a Human chip after the comparison. */
  actor?: string;
};

export function documentsHeaderFacts(): DocumentsHeaderFact[] {
  const sheets = roundSheetsThisShift();
  const facts: DocumentsHeaderFact[] = [
    {
      id: 'round-sheets',
      label: 'Round sheets · this shift',
      value: String(sheets.done),
      unit: `of ${sheets.total} checks`,
      comparison: sheets.flagged.length > 0
        ? [
            { text: `${sheets.flagged.length} flagged`, strong: true, tone: 'warn' },
            { text: ` · ${sheets.flagged.map(flaggedSymptom).join(' · ')}` },
          ]
        : [{ text: `${sheets.total - sheets.done} due`, strong: true }],
    },
  ];
  const next = TRAINING_MODULES.find((doc) => doc.pendingRevision);
  const nextVersion = next ? pendingVersionOf(next) : undefined;
  if (next?.pendingRevision && nextVersion) {
    facts.push({
      id: 'next-publish',
      label: 'Next publish',
      value: `${next.id} ${nextVersion}`,
      comparison: [{ text: 'in review' }],
      actor: next.pendingRevision.reviewerName,
    });
  }
  return facts;
}

/** Each tab's headline, derived from the register, the library and the change queue. */
export function documentsTabHeadlines(): Record<DocumentsTab, string> {
  const matrix = signoffMatrix();
  const documents = allDocuments();
  const inReview = documents.filter((doc) => pendingVersionOf(doc)).length;
  return {
    compliance: `${matrix.required - matrix.current} open`,
    library: `${documents.length} documents${inReview > 0 ? ` · ${inReview} in review` : ''}`,
    ai: `${pendingChanges().length} pending`,
  };
}
